import { Checkbox, Tag, Tooltip } from 'antd';
import { Routes } from 'config/routes';
import { format } from 'date-fns';
import { IDonorRecord } from 'modules/dashboard/types/donor';
import { formatCsvDate } from 'modules/dashboard/utils/csvDate';
import { Link } from 'react-router-dom';

import DonorContactPopover from '../components/DonorContactPopover';
import { makeFilterDropdown } from '../components/FilterDropdown';
import {
  BLOOD_TYPE_OPTIONS,
  CONTACTED_FILTER_OPTIONS,
  DATE_FORMAT,
  LINK_COLOR,
  PENDING_DAYS_FILTER_OPTIONS,
  PENDING_DAYS_ROW_COLORS,
  SGL_DBL_OPTIONS,
  STATUS_COLORS,
  TYPE_COLORS,
} from '../constants';
import styles from '../styles.module.scss';
import { ColumnKey, ColumnRegistryEntry } from '../types';
import { getUniqueValues, toFilterOptions } from '../utils/filterUtils';
import {
  getDaysPending,
  getPendingDaysStatus,
} from '../utils/pendingDaysUtils';

function makeDateSorter(
  getDate: (row: IDonorRecord) => string | null | undefined
) {
  return (a: IDonorRecord, b: IDonorRecord) =>
    (getDate(a) ? new Date(getDate(a)!).getTime() : 0) -
    (getDate(b) ? new Date(getDate(b)!).getTime() : 0);
}

export const COLUMN_REGISTRY: Record<ColumnKey, ColumnRegistryEntry> = {
  registeredAt: {
    label: 'Date Registered',
    defaultVisible: true,
    buildTableColumn: ({ defaultSortDescend }) => ({
      title: 'Date Registered',
      dataIndex: 'registeredAt',
      key: 'registeredAt',
      width: 120,
      render: (date: string | null) =>
        date ? format(new Date(date), DATE_FORMAT) : '-',
      sorter: makeDateSorter((r) => r.registeredAt),
      defaultSortOrder: defaultSortDescend ? 'descend' : undefined,
    }),
    csvColumns: [
      {
        label: 'Date Registered',
        value: (d) => (d.registeredAt ? formatCsvDate(d.registeredAt) : ''),
      },
    ],
  },

  lastDonationDate: {
    label: 'Last Donation',
    defaultVisible: true,
    buildTableColumn: ({ periodStart, periodEnd }) => ({
      title: 'Last Donation',
      dataIndex: 'lastDonationDate',
      key: 'lastDonationDate',
      width: 120,
      render: (date: string | null) => {
        if (!date) return '-';
        const d = new Date(date);
        const isOutOfRange =
          periodStart && periodEnd && (d < periodStart || d > periodEnd);
        return isOutOfRange ? (
          <span className={styles.outOfRange}>{format(d, DATE_FORMAT)}</span>
        ) : (
          format(d, DATE_FORMAT)
        );
      },
      sorter: makeDateSorter((r) => r.lastDonationDate),
    }),
    csvColumns: [
      {
        label: 'Last Donation',
        value: (d) =>
          d.lastDonationDate ? formatCsvDate(d.lastDonationDate) : '',
        includeWhen: ({ isPending }) => !isPending,
      },
    ],
  },

  retiredDate: {
    label: 'Date Retired',
    defaultVisible: true,
    buildTableColumn: () => ({
      title: 'Date Retired',
      dataIndex: 'retiredDate',
      key: 'retiredDate',
      width: 110,
      render: (date: string | null) =>
        date ? format(new Date(date), DATE_FORMAT) : '-',
      sorter: makeDateSorter((r) => r.retiredDate),
    }),
    csvColumns: [
      {
        label: 'Date Retired',
        value: (d) => (d.retiredDate ? formatCsvDate(d.retiredDate) : ''),
        includeWhen: ({ isRetired }) => isRetired,
      },
    ],
  },

  ownerFullName: {
    label: 'Owner',
    defaultVisible: true,
    buildTableColumn: ({ donors }) => ({
      title: 'Owner',
      dataIndex: 'ownerFullName',
      key: 'ownerFullName',
      width: 150,
      render: (name: string | null, record: IDonorRecord) =>
        name && record.ownerShortId ? (
          <Link
            to={Routes.EditOwner.replace(':id', String(record.ownerShortId))}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: LINK_COLOR }}
          >
            {name}
          </Link>
        ) : (
          name || '-'
        ),
      sorter: (a, b) =>
        (a.ownerFullName || '').localeCompare(b.ownerFullName || ''),
      filterDropdown: makeFilterDropdown(
        toFilterOptions(getUniqueValues(donors, (d) => d.ownerFullName))
      ),
      onFilter: (value, record) => record.ownerFullName === value,
    }),
    csvColumns: [{ label: 'Owner', value: (d) => d.ownerFullName || '' }],
  },

  donorName: {
    label: 'Donor',
    defaultVisible: true,
    buildTableColumn: ({ donors }) => ({
      title: 'Donor',
      dataIndex: 'donorName',
      key: 'donorName',
      width: 120,
      render: (name: string, record: IDonorRecord) => (
        <Link
          to={Routes.DonorsProfile.replace(':id', String(record.donorShortId))}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: LINK_COLOR }}
        >
          {name}
        </Link>
      ),
      sorter: (a, b) => a.donorName.localeCompare(b.donorName),
      filterDropdown: makeFilterDropdown(
        toFilterOptions(getUniqueValues(donors, (d) => d.donorName))
      ),
      onFilter: (value, record) => record.donorName === value,
    }),
    csvColumns: [{ label: 'Donor', value: (d) => d.donorName }],
  },

  contactInfo: {
    label: 'Contact Info',
    defaultVisible: true,
    buildTableColumn: () => ({
      title: 'Contact Info',
      key: 'contactInfo',
      width: 220,
      render: (_: unknown, record: IDonorRecord) => {
        const cityState = [record.ownerCity, record.ownerState]
          .filter(Boolean)
          .join(' / ');
        return (
          <div style={{ lineHeight: 1.4, fontSize: 12 }}>
            {record.ownerPhone && <div>{record.ownerPhone}</div>}
            {record.ownerEmail && (
              <div>
                <a
                  href={`mailto:${record.ownerEmail}`}
                  style={{ color: LINK_COLOR }}
                >
                  {record.ownerEmail}
                </a>
              </div>
            )}
            {cityState && <div style={{ color: '#6e7a8a' }}>{cityState}</div>}
            {record.ownerContactPreferences && (
              <div style={{ color: '#8c8c8c', fontStyle: 'italic' }}>
                Prefers: {record.ownerContactPreferences}
              </div>
            )}
            {!record.ownerPhone &&
              !record.ownerEmail &&
              !cityState &&
              !record.ownerContactPreferences &&
              '-'}
          </div>
        );
      },
    }),
    csvColumns: [
      { label: 'Phone', value: (d) => d.ownerPhone || '' },
      { label: 'Email', value: (d) => d.ownerEmail || '' },
      {
        label: 'City / State',
        value: (d) => [d.ownerCity, d.ownerState].filter(Boolean).join(' / '),
      },
      { label: 'Contact Pref', value: (d) => d.ownerContactPreferences || '' },
    ],
  },

  location: {
    label: 'Location',
    defaultVisible: true,
    buildTableColumn: ({ donors }) => ({
      title: 'Location',
      key: 'location',
      width: 140,
      render: (_: unknown, record: IDonorRecord) =>
        record.ownerAssignedLocations || record.location || '-',
      sorter: (a, b) =>
        (a.ownerAssignedLocations || a.location).localeCompare(
          b.ownerAssignedLocations || b.location
        ),
      filterDropdown: makeFilterDropdown(
        toFilterOptions(
          getUniqueValues(donors, (d) => d.ownerAssignedLocations || d.location)
        )
      ),
      onFilter: (value, record) =>
        (record.ownerAssignedLocations || record.location) === value,
    }),
    csvColumns: [
      {
        label: 'Location',
        value: (d) => d.ownerAssignedLocations || d.location || '',
      },
    ],
  },

  region: {
    label: 'Region',
    defaultVisible: true,
    buildTableColumn: ({ donors }) => ({
      title: 'Region',
      dataIndex: 'region',
      key: 'region',
      width: 120,
      render: (val: string | null) => val || '-',
      sorter: (a, b) => (a.region || '').localeCompare(b.region || ''),
      filterDropdown: makeFilterDropdown(
        toFilterOptions(getUniqueValues(donors, (d) => d.region))
      ),
      onFilter: (value, record) => record.region === value,
    }),
    csvColumns: [{ label: 'Region', value: (d) => d.region || '' }],
  },

  status: {
    label: 'Status',
    defaultVisible: true,
    buildTableColumn: ({ donors }) => ({
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <span style={{ color: STATUS_COLORS[status] || '#2f3a45' }}>
          {status}
        </span>
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
      filterDropdown: makeFilterDropdown(
        toFilterOptions(getUniqueValues(donors, (d) => d.status))
      ),
      onFilter: (value, record) => record.status === value,
    }),
    csvColumns: [{ label: 'Status', value: (d) => d.status }],
  },

  sglDbl: {
    label: 'SGL/DBL',
    defaultVisible: true,
    buildTableColumn: () => ({
      title: 'SGL/DBL',
      dataIndex: 'sglDbl',
      key: 'sglDbl',
      width: 80,
      align: 'center',
      render: (val: string) => (
        <Tag color={val === 'DBL' ? TYPE_COLORS.DBL : TYPE_COLORS.SGL}>
          {val}
        </Tag>
      ),
      sorter: (a, b) => a.sglDbl.localeCompare(b.sglDbl),
      filterDropdown: makeFilterDropdown(SGL_DBL_OPTIONS),
      onFilter: (value, record) => record.sglDbl === value,
    }),
    csvColumns: [{ label: 'SGL/DBL', value: (d) => d.sglDbl }],
  },

  breed: {
    label: 'Breed',
    defaultVisible: true,
    buildTableColumn: ({ donors }) => ({
      title: 'Breed',
      dataIndex: 'breed',
      key: 'breed',
      width: 120,
      render: (val: string | null) => val || '-',
      sorter: (a, b) => (a.breed || '').localeCompare(b.breed || ''),
      filterDropdown: makeFilterDropdown(
        toFilterOptions(getUniqueValues(donors, (d) => d.breed))
      ),
      onFilter: (value, record) => record.breed === value,
    }),
    csvColumns: [{ label: 'Breed', value: (d) => d.breed || '' }],
  },

  periodUnits: {
    label: 'Units',
    defaultVisible: true,
    buildTableColumn: ({ periodLabel }) => ({
      title: periodLabel ? (
        <span>
          Units
          <br />({periodLabel})
        </span>
      ) : (
        'Units'
      ),
      dataIndex: 'periodUnits',
      key: 'periodUnits',
      width: 120,
      align: 'center',
      sorter: (a, b) => a.periodUnits - b.periodUnits,
    }),
    csvColumns: [
      {
        label: 'Units',
        value: (d) => String(d.periodUnits),
        includeWhen: ({ isPending }) => !isPending,
      },
    ],
  },

  totalDonations: {
    label: 'Total Donations',
    defaultVisible: true,
    buildTableColumn: () => ({
      title: 'Total Donations',
      dataIndex: 'totalDonations',
      key: 'totalDonations',
      width: 90,
      align: 'center',
      sorter: (a, b) => a.totalDonations - b.totalDonations,
    }),
    csvColumns: [
      {
        label: 'Total Donations',
        value: (d) => String(d.totalDonations),
        includeWhen: ({ isPending }) => !isPending,
      },
    ],
  },

  bloodTypeDisplay: {
    label: 'Blood Type',
    defaultVisible: true,
    buildTableColumn: () => ({
      title: 'Blood Type',
      dataIndex: 'bloodTypeDisplay',
      key: 'bloodTypeDisplay',
      width: 80,
      align: 'center',
      sorter: (a, b) => a.bloodTypeDisplay.localeCompare(b.bloodTypeDisplay),
      filterDropdown: makeFilterDropdown(BLOOD_TYPE_OPTIONS),
      onFilter: (value, record) => record.bloodTypeDisplay === value,
    }),
    csvColumns: [{ label: 'Blood Type', value: (d) => d.bloodTypeDisplay }],
  },

  daysPending: {
    label: 'Days',
    defaultVisible: true,
    buildTableColumn: ({ pendingDaysBreakpoints }) => {
      const bp = pendingDaysBreakpoints!;
      return {
        title: 'Days',
        key: 'daysPending',
        width: 80,
        align: 'center',
        render: (_: unknown, record: IDonorRecord) =>
          getDaysPending(record.registeredAt),
        onCell: (record: IDonorRecord) => {
          const status = getPendingDaysStatus(
            getDaysPending(record.registeredAt),
            bp
          );
          return {
            style: { backgroundColor: PENDING_DAYS_ROW_COLORS[status] },
          };
        },
        sorter: (a: IDonorRecord, b: IDonorRecord) =>
          getDaysPending(a.registeredAt) - getDaysPending(b.registeredAt),
        defaultSortOrder: 'descend' as const,
        filterDropdown: makeFilterDropdown(PENDING_DAYS_FILTER_OPTIONS),
        onFilter: (value: unknown, record: IDonorRecord) =>
          getPendingDaysStatus(getDaysPending(record.registeredAt), bp) ===
          value,
      };
    },
    csvColumns: [
      {
        label: 'Days Pending',
        value: (d) => String(getDaysPending(d.registeredAt)),
        includeWhen: ({ isPending }) => isPending,
      },
      {
        label: 'Days Status',
        value: (d, ctx) => {
          const bp = ctx.pendingDaysBreakpoints ?? [30, 45];
          const labels = ctx.pendingDaysLabels ?? {
            low: 'On Track',
            mid: 'Approaching',
            high: 'Overdue',
          };
          const status = getPendingDaysStatus(
            getDaysPending(d.registeredAt),
            bp
          );
          return status === 'onTrack'
            ? labels.low
            : status === 'approaching'
            ? labels.mid
            : labels.high;
        },
        includeWhen: ({ isPending }) => isPending,
      },
    ],
  },

  contacted: {
    label: 'Contacted',
    defaultVisible: true,
    buildTableColumn: ({ contactedMap, onContactedChange, isAdmin }) => ({
      title: 'Contacted',
      key: 'contacted',
      width: 110,
      align: 'center',
      filterDropdown: makeFilterDropdown(CONTACTED_FILTER_OPTIONS),
      onFilter: (value: unknown, record: IDonorRecord) => {
        const isContacted = contactedMap?.[record.donorId]?.contacted ?? false;
        return value === 'yes' ? isContacted : !isContacted;
      },
      render: (_: unknown, record: IDonorRecord) => {
        const entry = contactedMap?.[record.donorId];
        const isContacted = entry?.contacted ?? false;
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip
              title={
                isContacted ? 'Mark as not contacted' : 'Mark as contacted'
              }
            >
              <Checkbox
                checked={isContacted}
                onChange={(e) =>
                  onContactedChange?.(record.donorId, e.target.checked)
                }
                className={styles.contactedCheckbox}
              />
            </Tooltip>
            <DonorContactPopover
              donorId={record.donorId}
              donorName={record.donorName}
              hasContacts={!!entry?.note}
              isAdmin={isAdmin}
            />
          </div>
        );
      },
    }),
    csvColumns: [],
  },
};

export const COLUMN_VISIBILITY_LABELS: { key: ColumnKey; label: string }[] = (
  Object.entries(COLUMN_REGISTRY) as [ColumnKey, ColumnRegistryEntry][]
).map(([key, entry]) => ({ key, label: entry.label }));

export const DEFAULT_VISIBLE_COLUMNS: Record<ColumnKey, boolean> =
  Object.fromEntries(
    (Object.entries(COLUMN_REGISTRY) as [ColumnKey, ColumnRegistryEntry][]).map(
      ([key, entry]) => [key, entry.defaultVisible]
    )
  ) as Record<ColumnKey, boolean>;
